import { combineReducers } from 'redux';
import produce from 'immer';
import {
  ActivityActionType,
  ActivityAction,
  ActivityEditingDetails,
} from '../../actions/activityActions';

const loading = (state = false, action: ActivityAction) => {
  switch (action.type) {
    case ActivityActionType.GET_ACTIVITIES:
    case ActivityActionType.GET_WORKSPACE_ACTIVITIES:
      return true;
    case ActivityActionType.GET_WORKSPACE_ACTIVITIES_OK:
    case ActivityActionType.GET_WORKSPACE_ACTIVITIES_ERROR:
    case ActivityActionType.GET_ACTIVITIES_OK:
    case ActivityActionType.GET_ACTIVITIES_ERROR:
      return false;
    default:
      return state;
  }
};

const fetching = (state = false, action: ActivityAction) => {
  switch (action.type) {
    case ActivityActionType.SILENT_GET_ACTIVITIES:
    case ActivityActionType.GET_ACTIVITIES:
    case ActivityActionType.GET_WORKSPACE_ACTIVITIES:
      return true;
    case ActivityActionType.GET_WORKSPACE_ACTIVITIES_OK:
    case ActivityActionType.GET_WORKSPACE_ACTIVITIES_ERROR:
    case ActivityActionType.GET_ACTIVITIES_OK:
    case ActivityActionType.GET_ACTIVITIES_ERROR:
      return false;
    default:
      return state;
  }
};

const loadingError = (state = '', action: ActivityAction) => {
  switch (action.type) {
    case ActivityActionType.GET_ACTIVITIES:
      return '';
    case ActivityActionType.GET_ACTIVITIES_ERROR:
      return action.error;
    default:
      return state;
  }
};

const creating = (state = false, action: ActivityAction) => {
  switch (action.type) {
    case ActivityActionType.CREATE_ACTIVITY:
      return true;
    case ActivityActionType.CREATE_ACTIVITY_OK:
    case ActivityActionType.CREATE_ACTIVITY_ERROR:
      return false;
    default:
      return state;
  }
};

const createError = (state = '', action: ActivityAction) => {
  switch (action.type) {
    case ActivityActionType.CREATE_ACTIVITY:
      return '';
    case ActivityActionType.CREATE_ACTIVITY_ERROR:
      return action.error;
    default:
      return state;
  }
};

type LoadingState = { loading: boolean; loadingError: string };
type DeletingState = { deleting: boolean; deleteError: string };
type EditingStates = {
  [editingStatusKey in ActivityEditingDetails['statusKey']]: boolean;
};
type EditingErrors = {
  [editingErrorKey in ActivityEditingDetails['errorKey']]: string;
};

export type ActivityState = LoadingState & DeletingState & EditingStates & EditingErrors;

interface ActivityStatesById {
  [id: string]: ActivityState;
}

const byId = (state: ActivityStatesById = {}, action: ActivityAction) =>
  produce(state, (draft) => {
    switch (action.type) {
      case ActivityActionType.GET_ACTIVITIES_OK: {
        action.data.forEach((activity) => {
          draft[activity.id] = {} as ActivityState;
        });

        break;
      }
      case ActivityActionType.CREATE_ACTIVITY_OK: {
        draft[action.data.id] = {} as ActivityState;
        break;
      }
      case ActivityActionType.GET_ACTIVITY: {
        draft[action.activityId] = {
          ...draft[action.activityId],
          loading: true,
          loadingError: '',
        };
        break;
      }
      case ActivityActionType.GET_ACTIVITY_OK: {
        draft[action.activityId].loading = false;
        break;
      }
      case ActivityActionType.GET_ACTIVITY_ERROR: {
        draft[action.activityId].loading = false;
        draft[action.activityId].loadingError = action.error;
        break;
      }
      case ActivityActionType.EDIT_ACTIVITY: {
        draft[action.activityId][action.statusKey] = true;
        draft[action.activityId][action.errorKey] = '';
        break;
      }
      case ActivityActionType.EDIT_ACTIVITY_OK: {
        draft[action.activityId][action.statusKey] = false;
        break;
      }
      case ActivityActionType.EDIT_ACTIVITY_ERROR: {
        draft[action.activityId][action.statusKey] = false;
        draft[action.activityId][action.errorKey] = action.error;
        break;
      }
      case ActivityActionType.DELETE_ACTIVITY: {
        draft[action.activityId].deleting = true;
        draft[action.activityId].deleteError = '';
        break;
      }
      case ActivityActionType.DELETE_ACTIVITY_OK: {
        draft[action.activityId].deleting = false;
        break;
      }
      case ActivityActionType.DELETE_ACTIVITY_ERROR: {
        draft[action.activityId].deleting = false;
        draft[action.activityId].deleteError = action.error;
        break;
      }
      default:
        return state;
    }
  });

export default combineReducers({
  fetching,
  loading,
  loadingError,
  byId,
  creating,
  createError,
});
